import { type ComponentType, type LazyExoticComponent } from 'react';
import { SKIN, type Skin } from '../../types/enums';
import { lazyWithRetry } from '../../utils/lazyWithRetry';

type PageProps = Record<string, never>;
type PageModule = {
  default: ComponentType<PageProps>;
};

export type PageModuleLoader = () => Promise<PageModule>;
export type PageModuleMap = Record<string, PageModuleLoader>;

const DEFAULT_PAGES_BASE_PATH = '../../components/pages';
const GOLA_PAGES_BASE_PATH = '../../components/skins/gola/pages';
const NEOBET_PAGES_BASE_PATH = '../../components/skins/neobet/pages';

const defaultPageModules = import.meta.glob([
  '../../components/pages/*.tsx',
  '!../../components/pages/*.test.tsx',
]) as PageModuleMap;
const golaPageModules = import.meta.glob([
  '../../components/skins/gola/pages/*.tsx',
  '!../../components/skins/gola/pages/*.test.tsx',
]) as PageModuleMap;
const neobetPageModules = import.meta.glob([
  '../../components/skins/neobet/pages/*.tsx',
  '!../../components/skins/neobet/pages/*.test.tsx',
]) as PageModuleMap;

const SKIN_PAGE_BASE_PATHS: Partial<Record<Skin, string>> = {
  [SKIN.GOLA]: GOLA_PAGES_BASE_PATH,
  [SKIN.NEOBET]: NEOBET_PAGES_BASE_PATH,
};

const skinPageModules: Partial<Record<Skin, PageModuleMap>> = {
  [SKIN.GOLA]: golaPageModules,
  [SKIN.NEOBET]: neobetPageModules,
};

const buildPageModulePath = (basePath: string, fileName: string) => `${basePath}/${fileName}.tsx`;

export const getPageLoaderFromMaps = (
  fileName: string,
  basePath: string,
  moduleMap: PageModuleMap
): PageModuleLoader => {
  const loader = moduleMap[buildPageModulePath(basePath, fileName)];

  if (!loader) {
    throw new Error(`Page module not found for "${fileName}" in "${basePath}"`);
  }

  return loader;
};

export const getSkinPageLoaderFromMaps = (
  fileName: string,
  skin: Skin,
  pageModules: Partial<Record<Skin, PageModuleMap>>
): PageModuleLoader | undefined => {
  const moduleMap = pageModules[skin];

  if (!moduleMap) {
    return undefined;
  }

  const basePath = SKIN_PAGE_BASE_PATHS[skin];

  if (!basePath) {
    return undefined;
  }

  return moduleMap[buildPageModulePath(basePath, fileName)];
};

const createLazyPageComponent = (loader: PageModuleLoader): LazyExoticComponent<ComponentType<PageProps>> =>
  lazyWithRetry(loader);

export const createSharedLazyPage = (fileName: string) =>
  createLazyPageComponent(getPageLoaderFromMaps(fileName, DEFAULT_PAGES_BASE_PATH, defaultPageModules));

export const createSkinLazyPage = (fileName: string, skin: Skin) => {
  const loader = getSkinPageLoaderFromMaps(fileName, skin, skinPageModules);

  return loader ? createLazyPageComponent(loader) : undefined;
};
